import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { HardDrive, Loader2, X } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { energyService } from '../../services/energyService';
import { formatBytes } from '../../utils/formatBytes';
import { logger } from '../../utils/logger';
import { ENERGY_UPDATED_EVENT } from '../../hooks/useEnergyTank';

// ─────────────────────────────────────────────────────────────────────────
// GrayStackModal — what the reservation is MADE OF (Gray Stack #302).
//
// The tank says how many points are reserved for storage; clicking the gray
// figure says why. On top, the Graystack (#465): the same Gray points split into
// Consumed (behind us this cycle) and Reserved (days left + 15-day grace). Five categories (Voice notes added by #465 — recordings
// have no document row, so before #465 they were invisible), each with a byte
// weight and the points that weight costs per month. Rendered in the order the
// backend sends; an unknown category falls back to its raw name.
//
// PORTALED to document.body: the Energy panel lives inside the workspace's
// transformed panel ancestors, which become the containing block for
// `position: fixed` — an in-tree modal renders off-centre or clipped (#225).
//
// Points are printed VERBATIM from the backend's 2dp strings. Nothing here
// converts one to a Number, not even to add a separator: they are money-like
// quantities and the rounding contract belongs to the server.
// ─────────────────────────────────────────────────────────────────────────

const LABELS = {
    documents: ['energyStorageCatDocuments', 'Documents & PDFs'],
    images: ['energyStorageCatImages', 'Images & Screenshots'],
    voice: ['energyStorageCatVoice', 'Voice notes'],
    data: ['energyStorageCatData', 'Data Structures'],
    overhead: ['energyStorageCatOverhead', 'System Overhead'],
};

const HINTS = {
    documents: ['energyStorageCatDocumentsHint', 'Files in your Bunker'],
    images: ['energyStorageCatImagesHint', 'Photos, receipts and screenshots'],
    voice: ['energyStorageCatVoiceHint', 'Voice messages from chat and Telegram'],
    data: ['energyStorageCatDataHint', 'Conversations, notes, boards, finances, contacts'],
    overhead: ['energyStorageCatOverheadHint', 'Older versions, deleted files and indexes'],
};

const Row = ({ entry, t }) => {
    const [labelKey, labelFallback] = LABELS[entry.category] || [null, entry.category];
    const [hintKey, hintFallback] = HINTS[entry.category] || [];
    const size = formatBytes(entry.bytes);
    return (
        <li
            data-testid="gray-stack-row"
            data-category={entry.category}
            data-bytes={entry.bytes}
            className="flex items-start justify-between gap-4 py-3 border-b border-pai-hairline last:border-0 min-w-0"
        >
            <span className="min-w-0">
                <span className="block text-[15px] font-semibold text-white break-words">
                    {labelKey ? t(labelKey, labelFallback) : labelFallback}
                </span>
                {hintKey && (
                    <span className="block text-[12.5px] text-pai-meta break-words mt-0.5">
                        {t(hintKey, hintFallback)}
                    </span>
                )}
            </span>
            <span className="text-right flex-shrink-0">
                <span className="block text-[15px] font-bold text-white tnum">
                    {`${entry.pts_per_month} ${t('energyPtsSuffix', 'pts')}`}
                </span>
                {size && (
                    <span className="block text-[12.5px] text-pai-meta tnum mt-0.5">{size}</span>
                )}
            </span>
        </li>
    );
};

// "16 May 2026" / "16 de mayo de 2026" — a date-only ISO string, read as a
// LOCAL calendar day (a bare `new Date('2026-05-16')` is UTC midnight and can
// render as the 15th west of Greenwich).
const formatDay = (iso, language) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return iso || '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-GB',
        { day: 'numeric', month: 'long', year: 'numeric' });
};

// Graystack — Consumed vs Reserved (#465 live Gray points). Both come from the
// same reservation the tank shows as Gray points, and add up to it exactly.
const Graystack = ({ g, t, language }) => (
    <section data-testid="graystack" className="mb-5 min-w-0">
        <div className="flex items-center justify-between gap-3 min-w-0">
            <span className="text-[15px] font-semibold text-white truncate">
                {t('energyGraystack', 'Graystack')}
            </span>
            <span data-testid="graystack-total" translate="no"
                className="text-[17px] font-extrabold text-white tnum tracking-[-0.02em] flex-shrink-0">
                {`${g.gray_pts} ${t('energyPtsSuffix', 'pts')}`}
            </span>
        </div>
        <p className="text-[12.5px] text-pai-meta break-words mt-1">
            {t('energyGraystackHint', 'Points held for your storage. They grow and shrink the moment you add or delete a file.')}
        </p>
        <ul className="mt-2 min-w-0">
            {[
                ['consumed', 'energyGraystackConsumed', 'Consumed', g.consumed_pts,
                    t('energyGraystackConsumedHint', 'The part of this cycle already behind us')],
                ['reserved', 'energyGraystackReserved', 'Reserved', g.reserved_pts,
                    t('energyGraystackReservedHint', '{days} days left in the cycle + {buffer}-day grace',
                        { days: g.days_left_in_cycle, buffer: g.buffer_days })],
            ].map(([key, labelKey, fallback, pts, hint]) => (
                <li key={key} data-testid={`graystack-${key}`} data-pts={pts}
                    className="flex items-start justify-between gap-4 py-2 min-w-0">
                    <span className="min-w-0">
                        <span className="block text-[14px] text-pai-secondary">{t(labelKey, fallback)}</span>
                        <span className="block text-[12px] text-pai-meta break-words">{hint}</span>
                    </span>
                    <span translate="no" className="text-[14px] font-bold text-white tnum flex-shrink-0">
                        {`${pts} ${t('energyPtsSuffix', 'pts')}`}
                    </span>
                </li>
            ))}
        </ul>
        {g.protected_until && (
            <p data-testid="graystack-protected" className="text-[12.5px] text-pai-meta break-words">
                {t('energyGraystackProtected', 'Your reserved points cover your storage until {date}.',
                    { date: formatDay(g.protected_until, language) })}
            </p>
        )}
    </section>
);

const GrayStackModal = ({ onClose }) => {
    const { t, language } = useLanguage();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let alive = true;
        energyService
            .getStorageBreakdown()
            .then((res) => {
                if (!alive) return;
                setData(res);
                // Opening the breakdown re-measures storage, which can move the
                // reservation — tell the tank so the two never disagree (#465).
                window.dispatchEvent(new CustomEvent(ENERGY_UPDATED_EVENT));
            })
            .catch((err) => {
                logger.warn('ENERGY', `storage breakdown failed: ${err?.message || err}`);
                if (alive) setError(true);
            });
        return () => { alive = false; };
    }, []);

    // ⚠️ EXACTLY two levels: body > scrim > dialog — <Modal> keeps that shape.
    // gray-stack.spec.js asserts `el.parentElement.parentElement === document.body`.
    // Escape is <Modal>'s (capture + stop), so the Energy panel's own Escape
    // handler never tears the panel down behind this modal (the #272 lesson).
    return (
        <Modal
            onClose={onClose}
            zIndex="z-[70]"
            ariaLabel={t('energyStorageBreakdown', 'Storage breakdown')}
            cardClassName="max-h-[85dvh] min-w-0"
            cardTestId="gray-stack-modal"
        >
                <header className="flex items-start justify-between gap-3 px-8 pt-7 pb-4">
                    <span className="flex items-start gap-3 min-w-0">
                        <HardDrive size={18} className="text-pai-meta flex-shrink-0 mt-1" />
                        <span className="min-w-0">
                            <span className="block text-[19px] font-bold text-white tracking-[-0.015em] truncate">
                                {t('energyStorageBreakdown', 'Storage breakdown')}
                            </span>
                            <span className="block text-[13px] text-pai-meta break-words mt-1">
                                {t('energyStorageBreakdownHint', 'What your Gray Stack is reserved against')}
                            </span>
                        </span>
                    </span>
                    <button
                        type="button"
                        data-testid="gray-stack-close"
                        onClick={onClose}
                        aria-label={t('close', 'Close')}
                        className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                    >
                        <X size={18} />
                    </button>
                </header>

                <div className="px-8 pb-7 min-w-0">
                    {error ? (
                        <p data-testid="gray-stack-error" className="py-8 text-center text-[13px] text-pai-meta">
                            {t('energyError', "Couldn't load your energy data.")}
                        </p>
                    ) : !data ? (
                        <p className="flex items-center justify-center gap-2 py-8 text-[13px] text-pai-meta">
                            <Loader2 size={14} className="animate-spin" />
                            {t('energyLoading', 'Loading…')}
                        </p>
                    ) : (
                        <>
                            {data.graystack && <Graystack g={data.graystack} t={t} language={language} />}
                            <ul className="min-w-0 border-t border-pai-hairline">
                                {(data.categories || []).map((entry) => (
                                    <Row key={entry.category} entry={entry} t={t} />
                                ))}
                            </ul>
                            <div
                                data-testid="gray-stack-total"
                                className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-pai min-w-0"
                            >
                                <span className="text-[15px] font-semibold text-pai-secondary truncate">
                                    {t('energyStorageTotal', 'Total per month')}
                                </span>
                                <span className="text-right flex-shrink-0">
                                    <span className="block text-[17px] font-extrabold text-white tnum tracking-[-0.02em]">
                                        {`${data.total_pts_per_month} ${t('energyPtsSuffix', 'pts')}`}
                                    </span>
                                    {formatBytes(data.total_bytes) && (
                                        <span className="block text-[12.5px] text-pai-meta tnum mt-0.5">
                                            {formatBytes(data.total_bytes)}
                                        </span>
                                    )}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
    );
};

export default GrayStackModal;
